import type { LabsDirectCheckoutItemType } from "@shared/types/graphqlGenerated";
import { isProduction, isRemix } from "@shared/utils/fullscriptEnv/fullscriptEnv";

const EE_BASE_AUTH_URL = isProduction() ? "" : "/emerson";

// Define base paths for routes
const RUPA_MIGRATION_AUTH_BASE = "/o/migrate";
const RUPA_MIGRATION_UNAUTH_BASE = "/rupa_migration";

const PATIENT_CATALOG_BASE = isRemix() ? "/u" : "";

const DIRECT_LABS_CHECKOUT_SEGMENTS: Record<LabsDirectCheckoutItemType, string> = {
  PANEL: "panel",
  TEST: "test",
};

const sharedRoutes = {
  root: "/",
  stopImpersonating: "/a/sessions/stop_impersonating",
  journeyWebsite: (slug: string) => `/j/${slug}`,
  journeyOfferItem: (slug: string, offerItemId: string) => `/j/${slug}/${offerItemId}`,
  journeyCheckout: (slug: string, journeyId: string) => `/checkout/${slug}/journey/${journeyId}`,
  directLabsCheckout: (slug: string, itemType: LabsDirectCheckoutItemType, itemId: string) =>
    `/checkout/${slug}/labs/${DIRECT_LABS_CHECKOUT_SEGMENTS[itemType]}/${encodeURIComponent(itemId)}`,
  login: "/login",
  logout: "/logout",
  accounts: "/accounts",
  emerson: {
    base: EE_BASE_AUTH_URL,
  },
  patientTreatmentPlans: (id: string) => `/o/patients/${id}/treatment_plans`,
  careCenterCatalog: "/o/care-center/products",
  careCenterProductDetail: (productId: string) => `/o/care-center/products/${productId}`,
  wholesaleCatalog: "/o/wholesale/shop",
  patientCatalog: "/u/catalog",
  patientCatalogBase: `${PATIENT_CATALOG_BASE}/catalog`,
  thirdPartyPhlebotomyProviders: (labRecommendationId: string) =>
    `${PATIENT_CATALOG_BASE}/labs/recommendation/${labRecommendationId}/third-party-providers`,
  labsReflexHelp: "https://support.fullscript.com/articles/recommending-reflex-testing",
  // Routes for the rupa migration pages
  rupaMigration: {
    unauthenticated: {
      root: "/",
      accountType: "/account-type",
      login: "/login",
      signup: "/signup",
      patient: "/patient",
      clerk: "/clerk",
      subprac: "/subprac",
      sendPatientActivationEmail: "/send-patient-activation-email",
    },
    authenticated: {
      authorize: `${RUPA_MIGRATION_AUTH_BASE}/authorize`,
      accountMatching: `${RUPA_MIGRATION_AUTH_BASE}/account-matching`,
      patientMatching: `${RUPA_MIGRATION_AUTH_BASE}/patient-matching`,
      labsRegistration: `${RUPA_MIGRATION_AUTH_BASE}/labs-registration`,
      confirmAccountDetails: `${RUPA_MIGRATION_AUTH_BASE}/confirm-account-details`,

      // TODO: not yet implemented
      connection: `${RUPA_MIGRATION_AUTH_BASE}/connection`,
      accounts: `${RUPA_MIGRATION_AUTH_BASE}/accounts`,
      scheduleMove: `${RUPA_MIGRATION_AUTH_BASE}/schedule-move`,

      onboarding: {
        labsRegistration: `${RUPA_MIGRATION_AUTH_BASE}/onboarding/labs-registration`,
        customFeeSetup: `${RUPA_MIGRATION_AUTH_BASE}/onboarding/custom-fee-setup`,
        confirmAccountDetails: `${RUPA_MIGRATION_AUTH_BASE}/onboarding/confirm-account-details`,
      },
    },
  },

  orchestrator: {
    socket: "/orchestrator/socket",
  },
  external: {
    support: "https://support.fullscript.com/",
    contactSupport: "https://support.fullscript.com/articles/how-to-contact-fullscript-support/",
    termsOfService: "https://fullscript.com/terms-of-service",
    termsOfServicePatients: "https://fullscript.com/terms-of-service#terms2",
    labsTermsOfService: "https://fullscript.com/labs-terms-of-service",
    labsTermsOfServicePatients: "https://fullscript.com/labs-terms-of-service#terms2",
    privacyPolicy: "https://fullscript.com/privacy",
    legalTermsOfService: "https://fullscript.com/legal/terms-of-service",
    legalPrivacyStatement: "https://fullscript.com/legal/privacy",
    accessibility: "https://fullscript.com/accessibility",
    privacyRightsNotice: "https://fullscript.com/prn",
    // oxlint-disable-next-line @fullscript/no-s3-amazonaws - waiting on instructions as to what to convert this to
    autoshipHandout: "https://fs-marketing-files.s3.amazonaws.com/handouts/autoship-education.pdf",
    shippingPolicyWholesale:
      "https://support.fullscript.com/hc/en-us/articles/360047665712-Wholesale-shipping-policy",
    shippingPolicyHeatSensitive:
      "https://support.fullscript.com/hc/en-us/articles/360029182252-Shipping-heat-sensitive-products",
    patientDiscountsExplained:
      "https://support.fullscript.com/articles/im-a-patient-am-i-receiving-a-discount-off-my-order/",
    payingForOrder: "https://support.fullscript.com/articles/paying-for-your-order/",
    shippingPolicyRetail: "https://support.fullscript.com/articles/patient-shipping-policy/",
    refundPolicy: "https://support.fullscript.com/hc/en-us/articles/360020956252-Refunds-returns",
    protocolsMarketingPage:
      "https://support.fullscript.com/hc/en-us/articles/360046986612-Sharing-wellness-protocols-with-patients",
    profitDispensariesSupportPage: "https://support.fullscript.com/articles/profit-dispensaries/",
    wellevateMigrationSupport:
      "https://support.fullscript.com/hc/en-us/sections/13558530393115-Wellevate-migration",
    dispensaryAndPatientDiscountExplained:
      "https://support.fullscript.com/articles/dispensary-individual-patient-discounts/",
    legacyDiscountExplained:
      "https://support.fullscript.com/articles/dispensary-individual-patient-discounts/",
    growingYourPracticeRevenue:
      "https://support.fullscript.com/articles/growing-your-practice-revenue/",
    autoshipDiscountSettingsExplained:
      "https://support.fullscript.com/articles/autoship-save-discount/",
    autoshipTermsConditionsExplained: "https://fullscript.com/autoship-terms",
    autoshipTermsConditions: "https://fullscript.com/legal/autoship-terms",
    autoshipDiscountExplained:
      "https://support.fullscript.com/articles/autoship-save-discount/#h_01H32WBESQY7Y3HABD27X3FHA8",
    patients: "https://fullscript.com/patients",
    connect: "https://fullscript.com/connect",
    supplementQuality: "https://fullscript.com/blog/fullscript-quality-assurance",
    trackingDelilvery: "https://support.fullscript.com/sections/tracking-delivery/",
    howToVideo: "https://support.fullscript.com/sections/patient-videos/",
    returnsAndReplacements: "https://support.fullscript.com/sections/returns-replacements/",
    autoshipProgram:
      "https://support.fullscript.com/articles/auto-refills-with-fullscript-how-it-works",
    evidenceBasedDecisionSupport: "https://fullscript.com/blog/evidence-based-decision-support",
    emersonTerms: {
      termsOfService: "https://emersonecologics.com/terms-of-sale",
      privacyPolicy: "https://emersonecologics.com/privacy-policy",
    },
    studentOrdering: "https://fullscript.com/for/student-ordering#video",
    demo: "https://fullscript.com/demo",
    stripeConnectedAccountAgreement: "https://stripe.com/legal/connect-account",
    dispensarySalesTaxUS:
      "https://support.fullscript.com/hc/en-us/articles/13283882528923-US-dispensaries-Sales-tax",
    dispensarySalesTaxCA:
      "https://support.fullscript.com/hc/en-us/articles/13279981759771-Canadian-dispensaries-Sales-tax",
    prop65Warnings: "http://www.p65warnings.ca.gov",
    emerson: {
      support: "https://help.emersonecologics.com/",
      contactSupport: "https://help.emersonecologics.com/articles/contact-support/",
    },
    payloadGraphQL: {
      production: "https://fs-control.fullscript.cloud/api/graphql",
      staging: "https://fs-control-staging.s.fullscript.io/api/graphql",
    },
    labsToolkit: "https://fullscript.com/labs",
    wholesale2024: "https://fullscript.com/lp/wholesale-2024",
    canadaPostStrike: "https://support.fullscript.com/articles/canada-post-strike/",
    enablingMultiFactorAuthenticationSMSOption:
      "https://support.fullscript.com/articles/securing-your-practitioner-account-with-multi-factor-verification/#enabling-multi-factor-authentication-sms-option",
    enablingMultiFactorAuthenticationEmailOption:
      "https://support.fullscript.com/articles/securing-your-practitioner-account-with-multi-factor-verification/#enabling-multi-factor-authentication-email-option",
    enablingMultiFactorAuthenticationAuthenticatorAppOption:
      "https://support.fullscript.com/articles/securing-your-practitioner-account-with-multi-factor-verification/#enabling-multi-factor-authentication-authenticator-app-option",
    loggingInWithATimeCode:
      "https://support.fullscript.com/articles/securing-your-practitioner-account-with-multi-factor-verification/#logging-in-with-a-one-time-code",
    qualityProgram: {
      preferred: "https://fullscript.com/lp/quality-badge#top-pick-badge",
      qualityBadge: "https://fullscript.com/lp/quality-badge",
      emersonQualityBadge: "https://go.emersonecologics.com/quality",
    },
    remixRoutesDocumentation:
      "https://engineering-docs.fullscript.cloud/React/Remix/routing_in_remix_vs_react-router",
  },
} as const;

export { sharedRoutes, RUPA_MIGRATION_UNAUTH_BASE };
